<template>
  <MainLayout>
    <template #header>
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-bold text-gray-900">
            Главный дэшборд
          </h1>
          <p class="text-sm text-gray-500 mt-1">
            Обзор ключевых показателей и активности
          </p>
        </div>

        <div class="flex items-center space-x-3">
          <button
            @click="loadDashboardData"
            :disabled="isLoading"
            class="btn btn-outline"
            title="Обновить данные"
          >
            <ArrowPathIcon class="h-4 w-4" :class="{ 'animate-spin': isLoading }" />
            Обновить
          </button>

          <button
            @click="showCustomizeModal = true"
            class="btn btn-outline"
            title="Настройки дашборда"
          >
            <Cog6ToothIcon class="h-4 w-4" />
            Настройки
          </button>
        </div>
      </div>
    </template>

    <!-- Основное содержимое -->
    <div class="space-y-6">
      <!-- Ключевые метрики -->
      <div v-if="visibleWidgets.includes('metrics')" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <MetricCard
          title="Всего лидов"
          :value="stats.total_leads"
          :change="stats.new_leads_today"
          change-label="новых сегодня"
          icon="UserGroupIcon"
          color="blue"
        />

<!--        <MetricCard-->
<!--          title="Контактов"-->
<!--          :value="stats.total_contacts"-->
<!--          :change="stats.new_contacts_today"-->
<!--          change-label="новых сегодня"-->
<!--          icon="PhoneIcon"-->
<!--          color="gray"-->
<!--        />-->

<!--        <MetricCard-->
<!--          title="Компаний"-->
<!--          :value="stats.total_companies"-->
<!--          :change="0"-->
<!--          change-label="новых сегодня"-->
<!--          icon="BuildingOfficeIcon"-->
<!--          color="indigo"-->
<!--        />-->
      </div>

      <!-- Диаграммы -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- Статусы лидов -->
        <div v-if="visibleWidgets.includes('leadsStatus')" class="card">
          <div class="card-header">
            <h3 class="text-lg font-medium text-gray-900">
              Статусы лидов
            </h3>
            <button
              @click="refreshLeadsStatusData"
              class="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
              title="Обновить"
            >
              <ArrowPathIcon class="h-4 w-4" />
            </button>
          </div>
          <div class="card-body">
            <LeadsStatusChart
              :data="leadsStatusData"
              @chartClick="handleLeadsStatusClick"
              @refresh="refreshLeadsStatusData"
            />
          </div>
        </div>

        <!-- График активности -->
        <div v-if="visibleWidgets.includes('activity')" class="card">
          <div class="card-header">
            <h3 class="text-lg font-medium text-gray-900">
              График активности
            </h3>
          </div>
          <div class="card-body">
            <ActivityChart
              :data="activityData"
              @periodChange="handleActivityPeriodChange"
            />
          </div>
        </div>
      </div>

      <!-- Диаграмма источников лидов (на всю ширину) -->
      <div v-if="visibleWidgets.includes('sources')" class="card">
        <div class="card-header">
          <h3 class="text-lg font-medium text-gray-900">
            Источники лидов
          </h3>
        </div>
        <div class="card-body">
          <BarChart
            :data="leadsSourceData"
            label-field="source"
            value-field="count"
            title="Количество лидов"
            :height="300"
            @chartClick="handleSourceClick"
          />
        </div>
      </div>

      <!-- Последние данные -->
<!--      <div v-if="visibleWidgets.includes('recentData')" class="grid grid-cols-1 gap-6">-->
<!--        &lt;!&ndash; Последние лиды &ndash;&gt;-->
<!--        <div class="card">-->
<!--          <div class="card-header">-->
<!--            <h3 class="text-lg font-medium text-gray-900">-->
<!--              Последние лиды-->
<!--            </h3>-->
<!--            <button-->
<!--              @click="navigateToLeads"-->
<!--              class="text-sm text-blue-600 hover:text-blue-800"-->
<!--            >-->
<!--              Все лиды →-->
<!--            </button>-->
<!--          </div>-->
<!--          <div class="card-body">-->
<!--            <InteractiveDataTable-->
<!--              :data="recentLeads"-->
<!--              :columns="leadsColumns"-->
<!--              :actions="leadsActions"-->
<!--              :loading="isLoadingLeads"-->
<!--              :show-pagination="false"-->
<!--              :show-filters="false"-->
<!--              compact-->
<!--            />-->
<!--          </div>-->
<!--        </div>-->
<!--      </div>-->

      <!-- Быстрые действия -->
      <div v-if="visibleWidgets.includes('quickActions')" class="card">
        <div class="card-header">
          <h3 class="text-lg font-medium text-gray-900">
            Быстрые действия
          </h3>
        </div>
        <div class="card-body">
          <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
            <button
              @click="navigateToCreateLead"
              class="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
            >
              <div class="text-center">
                <PlusIcon class="h-8 w-8 mx-auto text-gray-400 mb-2" />
                <span class="text-sm font-medium text-gray-700">Создать лид</span>
              </div>
            </button>

            <button
              @click="navigateToReports"
              class="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors"
            >
              <div class="text-center">
                <ChartBarIcon class="h-8 w-8 mx-auto text-gray-400 mb-2" />
                <span class="text-sm font-medium text-gray-700">Отчёты</span>
              </div>
            </button>

            <button
              @click="navigateToSettings"
              class="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-500 hover:bg-gray-50 transition-colors"
            >
              <div class="text-center">
                <Cog6ToothIcon class="h-8 w-8 mx-auto text-gray-400 mb-2" />
                <span class="text-sm font-medium text-gray-700">Настройки</span>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Модалка настройки дашборда -->
    <div v-if="showCustomizeModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div class="px-6 py-4 border-b border-gray-200">
          <div class="flex items-center justify-between">
            <h3 class="text-lg font-medium text-gray-900">Настройка дашборда</h3>
            <button
              @click="showCustomizeModal = false"
              class="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon class="h-6 w-6" />
            </button>
          </div>
        </div>

        <div class="px-6 py-4 space-y-6">
          <!-- Видимые виджеты -->
          <div>
            <h4 class="text-sm font-medium text-gray-900 mb-3">Видимые виджеты</h4>
            <div class="space-y-2">
              <label
                v-for="widget in availableWidgets"
                :key="widget.key"
                class="flex items-center"
              >
                <input
                  v-model="visibleWidgets"
                  :value="widget.key"
                  type="checkbox"
                  class="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span class="text-sm text-gray-700">{{ widget.label }}</span>
              </label>
            </div>
          </div>

          <!-- Автообновление -->
          <div>
            <h4 class="text-sm font-medium text-gray-900 mb-3">Автообновление</h4>
            <select
              v-model="autoRefreshInterval"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option :value="0">Отключено</option>
              <option :value="30">30 секунд</option>
              <option :value="60">1 минута</option>
              <option :value="300">5 минут</option>
              <option :value="600">10 минут</option>
            </select>
          </div>

          <!-- Тема -->
          <div>
            <h4 class="text-sm font-medium text-gray-900 mb-3">Тема</h4>
            <div class="flex space-x-4">
              <label class="flex items-center">
                <input
                  v-model="theme"
                  value="light"
                  type="radio"
                  class="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500"
                />
                <span class="text-sm text-gray-700">Светлая</span>
              </label>
              <label class="flex items-center">
                <input
                  v-model="theme"
                  value="dark"
                  type="radio"
                  class="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500"
                />
                <span class="text-sm text-gray-700">Тёмная</span>
              </label>
            </div>
          </div>
        </div>

        <div class="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button
            @click="resetSettings"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Сбросить
          </button>
          <button
            @click="saveSettings"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            Сохранить
          </button>
        </div>
      </div>
    </div>
  </MainLayout>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import {
  ArrowPathIcon,
  Cog6ToothIcon,
  ArrowDownTrayIcon,
  PlusIcon,
  ChartBarIcon,
  XMarkIcon,
  BuildingOfficeIcon
} from '@heroicons/vue/24/outline'

// Компоненты
import MainLayout from '@/components/layout/MainLayout.vue'
import MetricCard from '@/components/dashboard/MetricCard.vue'
import LeadsStatusChart from '@/components/charts/LeadsStatusChart.vue'
import ActivityChart from '@/components/charts/ActivityChart.vue'
import BarChart from '@/components/charts/BarChart.vue'
import CombinedChart from '@/components/charts/CombinedChart.vue'
import InteractiveDataTable from '@/components/tables/InteractiveDataTable.vue'

// Хранилища и композиции
import { useDataStore } from '@/stores/data'
import { useNotifications } from '@/composables/useNotifications'
import { chartUtils } from '@/composables/useChart'

// Утилиты для маппинга статусов
import { getLeadStatusName, getLeadSourceName, transformLeadStatusData } from '@/utils/bitrixStatusMapping'

const router = useRouter()
const dataStore = useDataStore()
const { showError, showSuccess } = useNotifications()

// Состояние
const isLoading = ref(false)
const isLoadingLeads = ref(false)
const isLoadingDeals = ref(false)
const showCustomizeModal = ref(false)
const autoRefreshInterval = ref(0)
const theme = ref('light')

// Настройки дашборда
const visibleWidgets = ref(['metrics', 'leadsStatus', 'activity', 'sources', 'combined', 'recentData'])
const availableWidgets = [
  { key: 'metrics', label: 'Ключевые метрики' },
  { key: 'leadsStatus', label: 'Статусы лидов' },
  { key: 'activity', label: 'График активности' },
  { key: 'sources', label: 'Источники лидов' },
  { key: 'combined', label: 'Комбинированная диаграмма' },
  { key: 'recentData', label: 'Последние данные' },
  { key: 'quickActions', label: 'Быстрые действия' }
]

// Данные
const stats = ref({
  total_leads: 0,
  total_deals: 0,
  total_contacts: 0,
  total_companies: 0,
  new_leads_today: 0,
  new_deals_today: 0,
  new_contacts_today: 0,
  total_deal_amount: 0,
  new_deal_amount_today: 0
})

const leadsStatusData = ref([])
const activityData = ref([])
const leadsSourceData = ref([])
const combinedData = ref([])
const recentLeads = ref([])
const recentDeals = ref([])

// Автообновление
let refreshInterval = null

// Функция форматирования статуса для таблиц
const formatLeadStatus = (statusCode) => {
  return getLeadStatusName(statusCode)
}

// Конфигурация колонок таблиц
const leadsColumns = [
  { key: 'TITLE', label: 'Название', sortable: true },
  {
    key: 'STATUS_ID',
    label: 'Статус',
    type: 'status',
    formatter: formatLeadStatus // Используем функцию маппинга
  },
  { key: 'ASSIGNED_BY_ID', label: 'Ответственный' },
  { key: 'DATE_CREATE', label: 'Дата создания', type: 'datetime', sortable: true }
]

const dealsColumns = [
  { key: 'TITLE', label: 'Название', sortable: true },
  { key: 'OPPORTUNITY', label: 'Сумма', type: 'currency', sortable: true },
  { key: 'STAGE_ID', label: 'Стадия' },
  { key: 'DATE_CREATE', label: 'Дата создания', type: 'datetime', sortable: true }
]

// Действия для таблиц
const leadsActions = [
  {
    key: 'view',
    label: 'Просмотр',
    icon: 'EyeIcon',
    class: 'text-blue-600 hover:text-blue-900',
    handler: (lead) => handleLeadClick(lead)
  }
]

const dealsActions = [
  {
    key: 'view',
    label: 'Просмотр',
    icon: 'EyeIcon',
    class: 'text-blue-600 hover:text-blue-900',
    handler: (deal) => handleDealClick(deal)
  }
]

// Вычисляемые свойства
const formatCurrency = computed(() => chartUtils.formatCurrency)

// Методы загрузки данных
const loadDashboardData = async () => {
  isLoading.value = true

  try {
    console.log('📊 Загрузка данных дэшборда...')

    // Загружаем быстрые статистики
    await loadQuickStats()

    // Загружаем данные для диаграмм параллельно
    await Promise.all([
      loadLeadsStatusData(),
      loadActivityData(),
      loadLeadsSourceData(),
      loadCombinedData(),
      loadRecentLeads(),
      loadRecentDeals()
    ])

    console.log('✅ Данные дэшборда загружены')
  } catch (error) {
    console.error('❌ Ошибка загрузки дэшборда:', error)
    showError('Ошибка загрузки данных дэшборда')
  } finally {
    isLoading.value = false
  }
}

const loadQuickStats = async () => {
  try {
    const data = await dataStore.getQuickStats()
    if (data) {
      stats.value = data
    }
  } catch (error) {
    console.error('Ошибка загрузки быстрой статистики:', error)
  }
}

const loadLeadsStatusData = async () => {
  try {
    console.log('📊 Загрузка статусов лидов из API...')
    
    const data = await dataStore.aggregateData({
      collection_name: 'leads',
      aggregation_type: 'count',
      group_by: 'STATUS_ID'
    })

    if (data && data.data && data.data.length > 0) {
      console.log('✅ Получены данные статусов лидов:', data.data.length, 'статусов')
      
      // Преобразуем коды статусов в читаемые названия
      const transformedData = transformLeadStatusData(data.data)
      leadsStatusData.value = transformedData
      
      console.log('✅ Статусы лидов преобразованы в читаемые названия')
    } else {
      console.warn('⚠️ Нет данных по статусам лидов')
      leadsStatusData.value = []
    }
  } catch (error) {
    console.error('❌ Ошибка загрузки статистики лидов:', error)
    leadsStatusData.value = []
  }
}

const loadActivityData = async () => {
  try {
    console.log('📊 Загрузка данных активности из API...')
    
    // Получаем статистику по лидам за последние 30 дней
    const leadsStats = await dataStore.getCollectionStats('leads')
    
    if (leadsStats && leadsStats.by_date) {
      console.log('✅ Получены данные активности лидов:', leadsStats.by_date.length, 'дней')
      
      // Преобразуем данные в нужный формат (только лиды и сделки)
      const activityByDate = leadsStats.by_date.map(item => ({
        date: item._id,
        leads: item.count,
        deals: 0 // Будем загружать отдельно
      }))
      
      // Создаем карту для быстрого поиска
      const activityMap = new Map(activityByDate.map(item => [item.date, item]))
      
      // Получаем статистику по сделкам
      try {
        const dealsStats = await dataStore.getCollectionStats('deals')
        if (dealsStats && dealsStats.by_date) {
          console.log('✅ Получены данные активности сделок:', dealsStats.by_date.length, 'дней')
          
          // Добавляем данные по сделкам
          dealsStats.by_date.forEach(item => {
            const existing = activityMap.get(item._id)
            if (existing) {
              existing.deals = item.count
            } else {
              activityMap.set(item._id, {
                date: item._id,
                leads: 0,
                deals: item.count
              })
            }
          })
        }
      } catch (error) {
        console.warn('⚠️ Не удалось загрузить статистику сделок для активности:', error)
      }
      
      // Преобразуем карту обратно в массив и сортируем по дате
      const finalData = Array.from(activityMap.values()).sort((a, b) => new Date(a.date) - new Date(b.date))
      
      // Заполняем пропущенные дни нулями (за последние 30 дней)
      const today = new Date()
      const thirtyDaysAgo = new Date(today)
      thirtyDaysAgo.setDate(today.getDate() - 29)
      
      const fullData = []
      for (let i = 0; i < 30; i++) {
        const currentDate = new Date(thirtyDaysAgo)
        currentDate.setDate(thirtyDaysAgo.getDate() + i)
        const dateStr = currentDate.toISOString().split('T')[0]
        
        const existingData = finalData.find(item => item.date === dateStr)
        if (existingData) {
          fullData.push(existingData)
        } else {
          fullData.push({
            date: dateStr,
            leads: 0,
            deals: 0
          })
        }
      }
      
      activityData.value = fullData
      console.log('📈 Данные активности подготовлены для', fullData.length, 'дней (лиды + сделки)')
    } else {
      console.warn('⚠️ Нет данных активности, используем пустой массив')
      activityData.value = []
    }
  } catch (error) {
    console.error('❌ Ошибка загрузки данных активности:', error)
    activityData.value = []
  }
}

const loadLeadsSourceData = async () => {
  try {
    console.log('📊 Загрузка источников лидов из API...')
    
    const data = await dataStore.aggregateData({
      collection_name: 'leads',
      aggregation_type: 'count',
      group_by: 'SOURCE_ID'
    })

    if (data && data.data && data.data.length > 0) {
      console.log('✅ Получены данные источников лидов:', data.data.length, 'источников')
      
      // Преобразуем коды источников в читаемые названия
      leadsSourceData.value = data.data.map(item => ({
        source: getLeadSourceName(item._id),
        originalCode: item._id, // Сохраняем оригинальный код для фильтрации
        count: item.count || 0
      })).sort((a, b) => b.count - a.count) // Сортируем по убыванию
      
      console.log('✅ Источники лидов преобразованы в читаемые названия')
    } else {
      console.warn('⚠️ Нет данных по источникам лидов')
      leadsSourceData.value = []
    }
  } catch (error) {
    console.error('❌ Ошибка загрузки источников лидов:', error)
    leadsSourceData.value = []
  }
}

const loadCombinedData = async () => {
  try {
    console.log('📊 Загрузка комбинированных данных (лиды + сделки + суммы)...')
    
    // Получаем статистику по лидам за последние 30 дней
    const leadsStats = await dataStore.getCollectionStats('leads')
    const combinedMap = new Map()
    
    // Добавляем данные по лидам
    if (leadsStats && leadsStats.by_date) {
      console.log('✅ Получены данные лидов для комбинированной диаграммы:', leadsStats.by_date.length, 'дней')
      
      leadsStats.by_date.forEach(item => {
        combinedMap.set(item._id, {
          date: item._id,
          leads: item.count,
          deals: 0,
          amount: 0
        })
      })
    }
    
    // Получаем статистику по сделкам и их суммам
    try {
      const dealsStats = await dataStore.getCollectionStats('deals')
      if (dealsStats && dealsStats.by_date) {
        console.log('✅ Получены данные сделок для комбинированной диаграммы:', dealsStats.by_date.length, 'дней')
        
        dealsStats.by_date.forEach(item => {
          const existing = combinedMap.get(item._id)
          if (existing) {
            existing.deals = item.count
            existing.amount = item.sum || 0 // Сумма сделок за день
          } else {
            combinedMap.set(item._id, {
              date: item._id,
              leads: 0,
              deals: item.count,
              amount: item.sum || 0
            })
          }
        })
      }
    } catch (error) {
      console.warn('⚠️ Не удалось загрузить статистику сделок для комбинированной диаграммы:', error)
    }
    
    // Преобразуем карту в массив и сортируем по дате
    const finalData = Array.from(combinedMap.values()).sort((a, b) => new Date(a.date) - new Date(b.date))
    
    // Заполняем пропущенные дни нулями (за последние 30 дней)
    const today = new Date()
    const thirtyDaysAgo = new Date(today)
    thirtyDaysAgo.setDate(today.getDate() - 29)
    
    const fullData = []
    for (let i = 0; i < 30; i++) {
      const currentDate = new Date(thirtyDaysAgo)
      currentDate.setDate(thirtyDaysAgo.getDate() + i)
      const dateStr = currentDate.toISOString().split('T')[0]
      
      const existingData = finalData.find(item => item.date === dateStr)
      if (existingData) {
        fullData.push(existingData)
      } else {
        fullData.push({
          date: dateStr,
          leads: 0,
          deals: 0,
          amount: 0
        })
      }
    }
    
    combinedData.value = fullData
    console.log('📈 Комбинированные данные подготовлены для', fullData.length, 'дней (лиды + сделки + суммы)')
  } catch (error) {
    console.error('❌ Ошибка загрузки комбинированных данных:', error)
    combinedData.value = []
  }
}

const loadRecentLeads = async () => {
  isLoadingLeads.value = true

  try {
    const data = await dataStore.filterData({
      collection_name: 'leads',
      page: 1,
      limit: 5,
      sort_field: 'DATE_CREATE',
      sort_direction: 'desc'
    })

    recentLeads.value = data?.data || []
  } catch (error) {
    console.error('Ошибка загрузки последних лидов:', error)
  } finally {
    isLoadingLeads.value = false
  }
}

const loadRecentDeals = async () => {
  isLoadingDeals.value = true

  try {
    const data = await dataStore.filterData({
      collection_name: 'deals',
      page: 1,
      limit: 5,
      sort_field: 'DATE_CREATE',
      sort_direction: 'desc'
    })

    recentDeals.value = data?.data || []
  } catch (error) {
    console.error('Ошибка загрузки последних сделок:', error)
  } finally {
    isLoadingDeals.value = false
  }
}

// Обработчики событий
const refreshStats = () => {
  loadDashboardData()
}

const refreshLeadsStatusData = () => {
  loadLeadsStatusData()
}

// ОБНОВЛЕННАЯ функция обработки клика по статусам лидов
const handleLeadsStatusClick = (data) => {
  console.log('Клик по статусу лидов:', data)

  // Теперь data.data._id содержит читаемое название статуса
  // А originalCode содержит исходный код статуса для фильтрации
  const statusFilter = data.data.originalCode || data.data._id

  console.log('Фильтрация по статусу:', statusFilter)

  // Переход к фильтрованным лидам по статусу
  router.push({
    path: '/data/leads',
    query: { status: statusFilter }
  })
}

const handleSourceClick = (data) => {
  console.log('Клик по источнику:', data)
  
  // Используем originalCode для фильтрации, если он есть
  const sourceFilter = data.data.originalCode || data.data.source
  
  console.log('Фильтрация по источнику:', sourceFilter)
  
  // Переход к фильтрованным лидам по источнику
  router.push({
    path: '/data/leads',
    query: { source: sourceFilter }
  })
}

const handleActivityPeriodChange = (period) => {
  console.log('Изменение периода активности:', period)
  // Перезагружаем данные для нового периода
  loadActivityData()
}

const handleCombinedPeriodChange = (period) => {
  console.log('Изменение периода комбинированной диаграммы:', period)
  loadCombinedData()
}

const handleDatasetToggle = (dataset) => {
  console.log('Переключение датасета:', dataset)
}

/**
 * Обработка клика по лиду - переход к детальному просмотру
 */
const handleLeadClick = (lead) => {
  console.log('Клик по лиду:', lead)

  // Проверяем наличие ID
  if (!lead || (!lead.ID && !lead._id)) {
    showError('Не удается определить ID лида')
    return
  }

  // Получаем ID лида (поддерживаем разные форматы)
  const leadId = lead.ID || lead._id

  try {
    // Переходим к детальному просмотру лида
    router.push({
      name: 'LeadDetails',
      params: { id: leadId }
    })

    console.log(`🔗 Переход к лиду: /data/leads/${leadId}`)
  } catch (error) {
    console.error('❌ Ошибка навигации к лиду:', error)
    showError('Ошибка перехода к детальному просмотру лида')
  }
}

/**
 * Обработка клика по сделке - переход к детальному просмотру
 */
const handleDealClick = (deal) => {
  console.log('Клик по сделке:', deal)

  // Проверяем наличие ID
  if (!deal || (!deal.ID && !deal._id)) {
    showError('Не удается определить ID сделки')
    return
  }

  // Получаем ID сделки (поддерживаем разные форматы)
  const dealId = deal.ID || deal._id

  try {
    // Переходим к детальному просмотру сделки
    router.push({
      name: 'DealDetails',
      params: { id: dealId }
    })

    console.log(`🔗 Переход к сделке: /data/deals/${dealId}`)
  } catch (error) {
    console.error('❌ Ошибка навигации к сделке:', error)
    showError('Ошибка перехода к детальному просмотру сделки')
  }
}

// Экспорт диаграмм
const exportChart = (chartType) => {
  console.log('Экспорт диаграммы:', chartType)
  showSuccess('Диаграмма экспортирована')
}

// Навигация
const navigateToLeads = () => router.push('/data/leads')
const navigateToDeals = () => router.push('/data/deals')
const navigateToContacts = () => router.push('/data/contacts')
const navigateToCreateLead = () => router.push('/data/leads/create')
const navigateToCreateDeal = () => router.push('/data/deals/create')
const navigateToReports = () => router.push('/reports')
const navigateToSettings = () => router.push('/settings')

// Автообновление
const setupAutoRefresh = () => {
  if (refreshInterval) {
    clearInterval(refreshInterval)
    refreshInterval = null
  }

  if (autoRefreshInterval.value > 0) {
    refreshInterval = setInterval(() => {
      console.log('🔄 Автообновление дашборда...')
      loadDashboardData()
    }, autoRefreshInterval.value * 1000)

    showSuccess(`Автообновление включено (${autoRefreshInterval.value} сек)`)
  }
}

// Настройки дашборда
const saveSettings = () => {
  const settings = {
    visibleWidgets: visibleWidgets.value,
    autoRefreshInterval: autoRefreshInterval.value,
    theme: theme.value
  }

  localStorage.setItem('dashboardSettings', JSON.stringify(settings))
  showCustomizeModal.value = false
  showSuccess('Настройки сохранены')

  // Применяем настройки
  setupAutoRefresh()
}

const resetSettings = () => {
  visibleWidgets.value = ['metrics', 'leadsStatus', 'activity', 'sources', 'combined', 'recentData']
  autoRefreshInterval.value = 0
  theme.value = 'light'

  localStorage.removeItem('dashboardSettings')
  showSuccess('Настройки сброшены')
}

const loadSettings = () => {
  try {
    const saved = localStorage.getItem('dashboardSettings')
    if (saved) {
      const settings = JSON.parse(saved)
      visibleWidgets.value = settings.visibleWidgets || visibleWidgets.value
      autoRefreshInterval.value = settings.autoRefreshInterval || 0
      theme.value = settings.theme || 'light'
    }
  } catch (error) {
    console.error('Ошибка загрузки настроек:', error)
  }
}

// Жизненный цикл
onMounted(() => {
  loadSettings()
  loadDashboardData()
  setupAutoRefresh()
})

onUnmounted(() => {
  if (refreshInterval) {
    clearInterval(refreshInterval)
  }
})
</script>